import { Box, useMediaQuery, useTheme } from '@ublend-npm/aulaui-core';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { AulaState, User } from '@core/types/state';
import {
  HANDIN_PROVIDER,
  LTI1P3_PROVIDER,
  QUIZZES_PROVIDER,
  TURNITIN_PROVIDER,
} from '@core/assignments/constants';
import NavigationBackHeader from '@app/components/presentational/common/NavigationBackHeader/NavigationBackHeader';
import pushRoute from '@app/utils/routing';
import { getAssignmentsBaseRoute } from '../utils/navigation';
import { Assignment } from '@core/assignments/types';
import { createStyles } from './SubmissionsResponsive.styles';
import SubmissionSidebar from './SubmissionsSidebar/SubmissionSidebar';
import InfoAlert from './InfoAlert';
import { NO_STUDENT_EXTENSION, NO_STUDENT_MAIN_COHORT } from '../constants';
import { ExtensionUser } from './ManageExtension/ManageExtension.types';
import AssignmentHeader from '../AssignmentHeader/AssignmentHeader';
import { defaultMemoize } from 'reselect';
import {
  formatGroupSubmissions,
  groupSubmissionsByDate,
} from './GroupSubmissions/formatGroupSubmissions';

const resolveAssignmentId = (assignment?: { id?: string; objectId?: string }) =>
  assignment?.id || assignment?.objectId || '';

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

/**
 * Fallback: derive the submission segment from Redux routing state.
 * Used only when the router hasn't injected submissionId via params
 * (e.g. on the base `.../submissions` route).
 */
const selectCurrentSubmissionId = (state: AulaState): string => {
  const { routing } = state;
  if (!routing) return '';
  const { pathname } = routing.locationBeforeTransitions;
  const idx = pathname.search('/submissions');
  if (idx === -1) return '';
  const [segment] = pathname
    .substring(idx)
    .replace('/submissions', '')
    .replace(/^\//, '')
    .split('/');
  return segment || '';
};

// ---------------------------------------------------------------------------
// Empty-cohort alerts
// ---------------------------------------------------------------------------
type EmptyCohortAlertProps = { isExtension: boolean };

const EmptyCohortAlert = ({ isExtension }: EmptyCohortAlertProps) => {
  const { TITLE, MESSAGE } = isExtension
    ? NO_STUDENT_EXTENSION
    : NO_STUDENT_MAIN_COHORT;

  return (
    <InfoAlert title={TITLE} sx={{ mt: '1.5rem' }}>
      {MESSAGE}
    </InfoAlert>
  );
};

// ---------------------------------------------------------------------------
// Dummy content — replace with real SubmissionDetails when ready
// ---------------------------------------------------------------------------
type SubmissionContentProps = {
  selectedAssignment?: Assignment | null;
  parentAssignment?: Assignment;
  isEducator?: boolean;
  spaceId: string;
  accessingAsStaff: boolean;
  totalStudentCount: number;
  totalSubmissionCount: number;
  showEmptyCohortAlert?: boolean;
  isExtension?: boolean;
};

const SubmissionContent = ({
  selectedAssignment,
  parentAssignment,
  isEducator = false,
  spaceId,
  accessingAsStaff,
  totalStudentCount,
  totalSubmissionCount,
  showEmptyCohortAlert = false,
  isExtension = false,
}: SubmissionContentProps) => {
  if (!selectedAssignment) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: { xs: '100%', lg: '792px' },
        maxWidth: { xs: '100%', md: '792px' },
        mx: 'auto',
        minHeight: '120px',
      }}
    >
      <AssignmentHeader
        assignment={selectedAssignment}
        parentAssignment={parentAssignment}
        page="assignments"
        isEducator={isEducator}
        accessingAsStaff={accessingAsStaff}
        currentSpaceId={spaceId}
        totalStudentCount={totalStudentCount}
        totalSubmissionCount={totalSubmissionCount}
      />
      {showEmptyCohortAlert && <EmptyCohortAlert isExtension={isExtension} />}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export type SubmissionAssignment = {
  description?: string;
  endDate?: string;
  gradesReleasedAt?: string;
  gradeType?: string;
  groupSet?: string;
  id: string;
  isHidden?: boolean;
  isAnonymised?: boolean;
  objectId?: string;
  parentAssignmentId?: string;
  provider?: string;
  shouldCloseOnDueDate?: boolean;
  startDate?: string;
  status?: string;
  students?: string[];
  studentsCount?: number;
  submissionsCount?: number;
  submissions?: string[];
  title: string;
  extensions?: any[];
};

type Props = {
  accessToken?: string;
  accessingAsStaff: boolean;
  assignment?: SubmissionAssignment;
  assignmentCreationError?: any;
  assignmentUpdateError?: any;
  createExtension: (params: any) => Promise<SubmissionAssignment | null>;
  editExtension: (params: any) => Promise<SubmissionAssignment | null>;
  extensions?: SubmissionAssignment[];
  fetchAssignments: (params: {
    accessToken: string;
    spaceId: string;
    user: User;
  }) => Promise<void>;
  fetchSubmissions: (params: {
    accessToken: string;
    assignmentId: string;
  }) => void;
  isFetchingAssignments: boolean;
  isEducator: boolean;
  loadStudents: (spaceId: string) => Promise<any[]>;
  params?: {
    assignmentId?: string;
    submissionId?: string;
  };
  spaceId: string;
  resetExtensionErrors: () => void;
  user: (User & ExtensionUser) | null;
  fetchAllSubmissionsIncludingExtensions: (params: {
    accessToken: string;
    assignmentsIds: string[];
  }) => void;
  hasFetchedAssignments: boolean;
  initialExtensionId: string | null;
  getSubmissions: (assignmentId: string) => any[];
  parentAssignmentSubmissions: any[];
  hasExtensions: boolean;
};

const SubmissionsResponsive = ({
  accessToken,
  accessingAsStaff,
  assignment,
  assignmentCreationError,
  assignmentUpdateError,
  createExtension,
  editExtension,
  extensions = [] as SubmissionAssignment[],
  fetchAssignments,
  fetchSubmissions,
  fetchAllSubmissionsIncludingExtensions,
  hasFetchedAssignments,
  isFetchingAssignments,
  isEducator,
  initialExtensionId,
  loadStudents,
  params,
  spaceId,
  resetExtensionErrors,
  user,
  getSubmissions,
  parentAssignmentSubmissions,
  hasExtensions,
}: Props) => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const {
    webAppFtSpaceArea202504,
    webappFtHandinAnonymousExtensions202110,
    webappFtTurnitinAnonymousExtensions202110,
  } = useFlags();
  const staffAlertOffset = webAppFtSpaceArea202504 && accessingAsStaff ? 56 : 0;
  const styles = createStyles(theme, staffAlertOffset);
  // Loader for assignments (Show skeleton loader when assignments are loading)
  // @ts-ignore
  const [assignmentLoading, setAssignmentsLoading] = useState<Boolean>(false);
  const assignmentId = params?.assignmentId;

  const assignmentKey = resolveAssignmentId(assignment);

  const refreshAssignments = async (savedExtension?: SubmissionAssignment) => {
    if (!accessToken || !spaceId || !user) return;
    await fetchAssignments({ accessToken, spaceId, user });
    if (savedExtension?.id) {
      // Match Submissionsv4's openExtension: make the saved extension active
      // and ensure its submissions are available immediately.
      const extensionId =
        savedExtension.provider === TURNITIN_PROVIDER
          ? savedExtension.objectId || savedExtension.id
          : savedExtension.id;
      setSelectedExtensionId(extensionId);
      fetchSubmissions({
        accessToken,
        assignmentId: extensionId,
      });
      pushRoute(
        `${getAssignmentsBaseRoute(
          spaceId
        )}/${assignmentId}/submissions/${extensionId}`
      );
    }
  };

  const loadAssignments = async (
    accessToken: string,
    spaceId: string,
    user: User
  ) => {
    try {
      setAssignmentsLoading(true);
      await fetchAssignments({ accessToken, spaceId, user });
    } catch (err) {
      throw err;
    } finally {
      setAssignmentsLoading(false);
    }
  };

  useEffect(() => {
    if (
      hasFetchedAssignments ||
      isFetchingAssignments ||
      !accessToken ||
      !spaceId ||
      !user
    ) {
      return;
    }
    loadAssignments(accessToken, spaceId, user);
  }, [
    accessToken,
    hasFetchedAssignments,
    fetchAssignments,
    isFetchingAssignments,
    spaceId,
    user,
  ]);

  // Prefer the submissionId injected by the router (available immediately on
  // hard refresh) and fall back to parsing the Redux routing state (used when
  // landing on the base `.../submissions` route that has no :submissionId param).
  const reduxSubmissionId = useSelector(selectCurrentSubmissionId);
  const currentSubmissionId = params?.submissionId ?? reduxSubmissionId;

  // Treat "no trailing segment" the same as "list" — this is the submissions
  // list view. A refresh on `.../submissions` should keep the list visible, not
  // redirect anywhere.
  const isListView = !currentSubmissionId || currentSubmissionId === 'list';

  const extensionIdFromRoute =
    currentSubmissionId && currentSubmissionId !== 'list'
      ? currentSubmissionId
      : '';

  // Get the extension id from the route or the initial extension id or the assignment id
  const currentExtensionId = useMemo(() => {
    if (extensionIdFromRoute) return extensionIdFromRoute;
    if (initialExtensionId) return initialExtensionId;
    if (isMdUp && assignmentKey) return assignmentKey;
    return '';
  }, [extensionIdFromRoute, initialExtensionId, isMdUp, assignmentKey]);

  const [selectedExtensionId, setSelectedExtensionId] = useState(
    currentExtensionId || assignmentKey || ''
  );

  useEffect(() => {
    if (currentExtensionId) {
      setSelectedExtensionId(currentExtensionId);
    }
  }, [currentExtensionId]);

  const anonymousExtensionsEnabled = (() => {
    if (!assignment?.isAnonymised) return true;
    if (assignment.provider === HANDIN_PROVIDER) {
      return Boolean(webappFtHandinAnonymousExtensions202110);
    }
    if (assignment.provider === TURNITIN_PROVIDER) {
      return Boolean(webappFtTurnitinAnonymousExtensions202110);
    }
    return false;
  })();
  const canCreateExtensions = Boolean(
    assignment &&
      isEducator &&
      !accessingAsStaff &&
      !assignment.groupSet &&
      assignment.provider !== QUIZZES_PROVIDER &&
      assignment.provider !== LTI1P3_PROVIDER &&
      anonymousExtensionsEnabled
  );
  const canEditExtensions = isEducator;

  useEffect(() => {
    if (!accessToken || !assignmentId) return;

    if (hasExtensions && assignment) {
      const assignmentsIds = [
        assignmentId,
        ...(assignment?.extensions || []).map(
          (extension) => extension.id || extension.objectId
        ),
      ].filter(Boolean) as string[];

      fetchAllSubmissionsIncludingExtensions({ accessToken, assignmentsIds });
      return;
    }

    fetchSubmissions({ accessToken, assignmentId });
  }, [
    accessToken,
    assignment,
    assignmentId,
    fetchAllSubmissionsIncludingExtensions,
    fetchSubmissions,
    hasExtensions,
  ]);

  const assignmentRoute = params?.assignmentId
    ? `${getAssignmentsBaseRoute(spaceId)}/${params.assignmentId}`
    : `${getAssignmentsBaseRoute(spaceId)}/list`;

  const submissionsBaseRoute = `${getAssignmentsBaseRoute(spaceId)}/${
    params?.assignmentId
  }/submissions`;

  // Desktop always shows sidebar + detail
  useEffect(() => {
    if (!isMdUp || !assignmentKey || !params?.assignmentId || !isListView) {
      return;
    }

    const targetExtensionId = initialExtensionId || assignmentKey;
    pushRoute(`${submissionsBaseRoute}/${targetExtensionId}`);
  }, [
    assignmentKey,
    initialExtensionId,
    isListView,
    isMdUp,
    params?.assignmentId,
    submissionsBaseRoute,
  ]);

  // Mobile deep-link from home (`?extensionId=`) opens the extension detail view.
  useEffect(() => {
    if (
      isMdUp ||
      !initialExtensionId ||
      !isListView ||
      !assignmentKey ||
      !extensions.length
    ) {
      return;
    }

    const extensionExists = extensions.some(
      (extension) =>
        String(extension.id) === String(initialExtensionId) ||
        String(extension.objectId) === String(initialExtensionId)
    );

    if (!extensionExists) return;

    pushRoute(`${submissionsBaseRoute}/${initialExtensionId}`);
  }, [
    assignmentKey,
    extensions,
    initialExtensionId,
    isListView,
    isMdUp,
    submissionsBaseRoute,
  ]);

  const selectExtension = (extension: SubmissionAssignment) => {
    const extensionId =
      extension.provider === TURNITIN_PROVIDER
        ? extension.objectId || extension.id
        : extension.id;

    setSelectedExtensionId(extensionId);
    if (accessToken) {
      fetchSubmissions({ accessToken, assignmentId: extensionId });
    }
    pushRoute(`${submissionsBaseRoute}/${extensionId}`);
  };

  const memoizedExtensionFinder = useCallback(
    (extension: SubmissionAssignment) =>
      extension.id === currentExtensionId ||
      extension.objectId === currentExtensionId,
    [currentExtensionId]
  );
  const currentAssignment = hasExtensions
    ? extensions?.find(memoizedExtensionFinder)
    : assignment;

  const currentSubmissions = currentAssignment
    ? getSubmissions(resolveAssignmentId(currentAssignment))
    : parentAssignmentSubmissions;

  const isGroupAssignment = !!assignment?.groupSet;

  const getFormattedGroupSubmissions = defaultMemoize((submissions) =>
    formatGroupSubmissions(submissions)
  );

  const submissionsData = isGroupAssignment
    ? getFormattedGroupSubmissions(parentAssignmentSubmissions)
    : currentSubmissions;
  const submissionGroups = groupSubmissionsByDate(
    submissionsData,
    (currentAssignment || assignment)?.endDate || ''
  );

  const { earlySubmissions, lateSubmissions, noSubmissions } = submissionGroups;
  // @totalCount gets the correct count for students/groups in individual & group assignments
  // submission length would give incorrect count for group assignments
  const totalStudentCount =
    earlySubmissions.length + lateSubmissions.length + noSubmissions.length;
  const totalSubmissionCount = earlySubmissions.length + lateSubmissions.length;

  const allSubmissions = Object.values(submissionGroups).flat();
  const isExtension = !!currentAssignment?.parentAssignmentId;
  const showEmptyCohortAlert =
    !isGroupAssignment && hasExtensions && allSubmissions.length === 0;

  // ── Desktop: sidebar + content always side-by-side ─────────────────────────
  if (isMdUp) {
    return (
      <Box sx={styles.desktopShell}>
        <Box sx={styles.leftWrapper}>
          <SubmissionSidebar
            accessToken={accessToken}
            assignment={assignment}
            assignmentCreationError={assignmentCreationError}
            assignmentUpdateError={assignmentUpdateError}
            backRoute={assignmentRoute}
            canCreateExtensions={canCreateExtensions}
            canEditExtensions={canEditExtensions}
            createExtension={createExtension}
            editExtension={editExtension}
            extensions={extensions}
            loadStudents={loadStudents}
            onExtensionSaved={refreshAssignments}
            onSelectExtension={selectExtension}
            resetExtensionErrors={resetExtensionErrors}
            selectedExtensionId={selectedExtensionId}
            spaceId={spaceId}
            user={user}
          />
        </Box>
        <Box sx={styles.contentArea}>
          <SubmissionContent
            selectedAssignment={
              currentAssignment
                ? (currentAssignment as Assignment)
                : (assignment as Assignment)
                ? (assignment as Assignment)
                : null
            }
            parentAssignment={
              hasExtensions && assignment
                ? (assignment as Assignment)
                : undefined
            }
            isEducator={isEducator}
            spaceId={spaceId}
            accessingAsStaff={accessingAsStaff}
            totalStudentCount={totalStudentCount}
            totalSubmissionCount={totalSubmissionCount}
            showEmptyCohortAlert={showEmptyCohortAlert}
            isExtension={isExtension}
          />
        </Box>
      </Box>
    );
  }

  // ── Mobile list view ────────────────────────────────────────────────────────
  if (isListView) {
    return (
      <Box sx={{ height: '100%' }}>
        <SubmissionSidebar
          accessToken={accessToken}
          assignment={assignment}
          assignmentCreationError={assignmentCreationError}
          assignmentUpdateError={assignmentUpdateError}
          backRoute={assignmentRoute}
          canCreateExtensions={canCreateExtensions}
          canEditExtensions={canEditExtensions}
          createExtension={createExtension}
          editExtension={editExtension}
          extensions={extensions}
          loadStudents={loadStudents}
          onExtensionSaved={refreshAssignments}
          onSelectExtension={selectExtension}
          resetExtensionErrors={resetExtensionErrors}
          selectedExtensionId={selectedExtensionId}
          spaceId={spaceId}
          user={user}
        />
      </Box>
    );
  }

  // ── Mobile detail view ──────────────────────────────────────────────────────
  // Back button goes to the submissions list (`.../submissions/list`).
  // White container wraps the detail content, matching the Assignment detail
  // mobile layout (AssignmentResponsive → pageContent).
  const submissionsListRoute = `${submissionsBaseRoute}/list`;

  return (
    <Box sx={styles.pageWrapper}>
      <Box sx={styles.buttonWrapper}>
        <NavigationBackHeader route={submissionsListRoute} />
      </Box>
      <Box sx={styles.pageContent}>
        <Box sx={styles.assignmentDetailsMobile}>
          <SubmissionContent
            selectedAssignment={
              currentAssignment
                ? (currentAssignment as Assignment)
                : (assignment as Assignment)
                ? (assignment as Assignment)
                : null
            }
            parentAssignment={
              hasExtensions && assignment
                ? (assignment as Assignment)
                : undefined
            }
            isEducator={isEducator}
            spaceId={spaceId}
            accessingAsStaff={accessingAsStaff}
            totalStudentCount={totalStudentCount}
            totalSubmissionCount={totalSubmissionCount}
            showEmptyCohortAlert={showEmptyCohortAlert}
            isExtension={isExtension}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SubmissionsResponsive;
